/**
 * Скелетон, пустое состояние, отказ запроса, встроенное сообщение и клавиша.
 *
 * Загрузка показывается скелетоном, а не спиннером (docs/04, §7.4): скелет
 * сохраняет структуру страницы, поэтому переход к данным не выглядит рывком.
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";

import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Ширина в процентах или пикселях: строки списка не должны быть одинаковыми. */
  width?: string;
  height?: string;
  rounded?: boolean;
}

export function Skeleton({
  className,
  width = "100%",
  height = "var(--pt-lh-ui)",
  rounded = false,
  style,
  ...props
}: SkeletonProps): React.ReactElement {
  return (
    <div
      aria-hidden
      className={cn(
        "animate-pulse bg-[var(--pt-bg-sunken)]",
        rounded ? "rounded-[var(--pt-radius-full)]" : "rounded-[var(--pt-radius-2)]",
        className,
      )}
      style={{ width, height, ...style }}
      {...props}
    />
  );
}

export interface SkeletonRowsProps {
  count?: number;
  className?: string;
}

export function SkeletonRows({ count = 5, className }: SkeletonRowsProps): React.ReactElement {
  // Разная длина строк: одинаковые полосы читаются как таблица, а не как загрузка
  const widths = ["92%", "78%", "85%", "64%", "88%", "72%"];
  return (
    <div className={cn("flex flex-col gap-[var(--pt-space-8)]", className)} aria-busy>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} width={widths[index % widths.length]} />
      ))}
      <span className="sr-only">Загрузка…</span>
    </div>
  );
}

export interface EmptyStateProps {
  /** Что здесь бывает — без «Нет данных». */
  title: string;
  /** Почему пусто и что с этим делать. */
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: EmptyStateProps): React.ReactElement {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        "px-[var(--pt-space-24)] pt-[var(--pt-space-40)] pb-[var(--pt-space-32)]",
        className,
      )}
    >
      {icon && <div className="mb-[var(--pt-space-12)] text-[var(--pt-text-tertiary)]">{icon}</div>}
      <p className="text-[var(--pt-fs-md)] leading-[var(--pt-lh-md)] font-medium text-[var(--pt-text-primary)]">
        {title}
      </p>
      {description && (
        <p className="mt-[var(--pt-space-4)] max-w-[420px] text-[var(--pt-fs-sm)] leading-[var(--pt-lh-sm)] text-[var(--pt-text-secondary)]">
          {description}
        </p>
      )}
      {action && <div className="mt-[var(--pt-space-16)]">{action}</div>}
    </div>
  );
}

/** Первая буква строчной: «Отчёт» в середине фразы пишется со строчной. */
function lowered(what: string): string {
  return what.charAt(0).toLowerCase() + what.slice(1);
}

export interface ErrorStateProps {
  /** Что не загрузилось, в винительном падеже: «Сценарии», «Список задач». */
  what: string;
  error?: unknown;
  onRetry?: () => void;
  className?: string;
}

/**
 * Запрос не удался.
 *
 * До сих пор такой случай выглядел как пустой список: «Сценариев нет» вместо
 * «не удалось загрузить». Человек делал вывод, что данные исчезли, и шёл их
 * заводить заново — при упавшем сервере или потерянной сети.
 */
export function ErrorState({
  what,
  error,
  onRetry,
  className,
}: ErrorStateProps): React.ReactElement {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center text-center",
        "px-[var(--pt-space-24)] pt-[var(--pt-space-40)] pb-[var(--pt-space-32)]",
        className,
      )}
    >
      <XCircle
        aria-hidden
        className="mb-[var(--pt-space-12)] size-6 text-[var(--pt-danger-text)]"
      />
      <p className="text-[var(--pt-fs-md)] leading-[var(--pt-lh-md)] font-medium text-[var(--pt-text-primary)]">
        {/* «Не удалось загрузить X» вместо «X не загрузились»: второе годится
            только для множественного числа, и отчёт в единственном получал
            «Отчёт не загрузились» */}
        Не удалось загрузить {lowered(what)}
      </p>
      <p className="mt-[var(--pt-space-4)] max-w-[420px] text-[var(--pt-fs-sm)] leading-[var(--pt-lh-sm)] text-[var(--pt-text-secondary)]">
        {message(error)}
      </p>
      {onRetry && (
        <div className="mt-[var(--pt-space-16)]">
          <Button variant="secondary" onClick={onRetry}>
            Повторить
          </Button>
        </div>
      )}
    </div>
  );
}

function message(error: unknown): string {
  // Текст ошибки от сервера полезнее общих слов: в нём видно, дело в правах,
  // в сети или в самой выборке
  const text = error instanceof Error ? error.message.trim() : "";
  return text === "" ? "Похоже, нет связи с сервером. Проверьте сеть и повторите." : text;
}

const alertVariants = cva(
  "flex gap-[var(--pt-space-8)] rounded-[var(--pt-radius-3)] border " +
    "px-[var(--pt-space-12)] py-[var(--pt-space-8)] " +
    "text-[var(--pt-fs-sm)] leading-[var(--pt-lh-sm)]",
  {
    variants: {
      tone: {
        info: "border-[var(--pt-info-border)] bg-[var(--pt-info-soft)] text-[var(--pt-info-text)]",
        success:
          "border-[var(--pt-success-border)] bg-[var(--pt-success-soft)] text-[var(--pt-success-text)]",
        warning:
          "border-[var(--pt-warning-border)] bg-[var(--pt-warning-soft)] text-[var(--pt-warning-text)]",
        danger:
          "border-[var(--pt-danger-border)] bg-[var(--pt-danger-soft)] text-[var(--pt-danger-text)]",
      },
    },
    defaultVariants: { tone: "info" },
  },
);

const ALERT_ICON = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
} as const;

export interface InlineAlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  title?: string;
}

export function InlineAlert({
  tone = "info",
  title,
  children,
  className,
  ...props
}: InlineAlertProps): React.ReactElement {
  const Icon = ALERT_ICON[tone ?? "info"];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn(alertVariants({ tone }), className)}
      {...props}
    >
      <Icon aria-hidden className="mt-[2px] size-[var(--pt-icon)] shrink-0" />
      <div className="min-w-0">
        {title && <p className="font-medium">{title}</p>}
        {children}
      </div>
    </div>
  );
}

export interface KbdProps extends React.HTMLAttributes<HTMLElement> {
  keys: readonly string[];
}

export function Kbd({ keys, className, ...props }: KbdProps): React.ReactElement {
  return (
    <span className={cn("inline-flex items-center gap-[var(--pt-space-2)]", className)} {...props}>
      {keys.map((key) => (
        <kbd
          key={key}
          className={cn(
            "inline-flex h-[18px] min-w-[18px] items-center justify-center",
            "rounded-[var(--pt-radius-2)] border border-[var(--pt-border-default)]",
            "bg-[var(--pt-bg-sunken)] px-[var(--pt-space-4)]",
            "font-[family-name:var(--pt-font-mono)] text-[var(--pt-fs-micro)]",
            "text-[var(--pt-text-secondary)]",
          )}
        >
          {key}
        </kbd>
      ))}
    </span>
  );
}
